import React, { useMemo } from "react";
import { Select, type SelectOptionData, type SelectProps, type SelectValue } from "@shared/ui/select";
import { formatUnresolvedIdForDisplay } from "../formatUnresolvedIdForDisplay";
import { useBusinessSelect } from "./useBusinessSelect";

type IdValue = string | number | boolean | undefined;

type SelectRawChangeValue<M extends boolean, V> = M extends true ? V[] : V | null;

export type BusinessSelectProps<
    TData extends object,
    Id extends IdValue,
    T extends object = TData,
    M extends boolean = false,
> = Omit<SelectProps<T, Id, M>, "options" | "getOptionData" | "value" | "onChange" | "isLoading" | "isRefreshing"> & {
    queryKey: readonly unknown[];
    loadOptions: (ctx: { signal?: AbortSignal }) => Promise<TData[]>;
    getOptionData: (option: T) => SelectOptionData<Id, unknown>;
    augmentOptions?: (dataOptions: TData[]) => T[];
    transformOptions?: (items: TData[]) => TData[];
    value: SelectValue<M, T | Id> | null;
    onChange?: (value: M extends true ? T[] : T | null, rawValue: SelectRawChangeValue<M, Id>) => void;
    optionsLoadEnabled?: boolean;
    optionsStaleTime?: number;
};

/**
 * Бизнес-селект с загрузкой опций через {@link useBusinessSelect}. \
 * Overlay: blocking (`isFetching && options == null`) vs soft refresh (`isFetching && options != null`).
 */
export function BusinessSelect<
    TData extends object,
    Id extends IdValue,
    T extends object = TData,
    M extends boolean = false,
>(props: BusinessSelectProps<TData, Id, T, M>) {
    const {
        queryKey,
        loadOptions,
        getOptionData,
        augmentOptions,
        transformOptions,
        value,
        multiple,
        disabled: disabledProp,
        onChange,
        optionsLoadEnabled,
        optionsStaleTime,
        statusSlot: statusSlotFromConsumer,
        ...selectProps
    } = props;

    const {
        options,
        getOptionData: getOptionDataFromHook,
        isLoading,
        needsResolution,
        resolvedValue,
        isUnresolved,
        unresolvedValueId,
        handleChange,
    } = useBusinessSelect<TData, Id, T, M>({
        queryKey,
        loadOptions,
        getOptionData,
        augmentOptions,
        transformOptions,
        disabled: disabledProp === true,
        value,
        multiple,
        onChange,
        optionsLoadEnabled,
        optionsStaleTime,
    });

    const defaultStatusSlot = useMemo(() => {
        if (isLoading === true && needsResolution === true) return "Загрузка...";
        if (isUnresolved === true && unresolvedValueId != null) {
            return `Не найдено (код: ${formatUnresolvedIdForDisplay(unresolvedValueId as Exclude<Id, undefined>)})`;
        }
        return undefined;
    }, [isLoading, needsResolution, isUnresolved, unresolvedValueId]);

    const statusSlot = statusSlotFromConsumer !== undefined ? statusSlotFromConsumer : defaultStatusSlot;

    const blockingOptionsLoading = isLoading === true && options == null;
    const backgroundOptionsRefreshing = isLoading === true && options != null;

    return (
        <Select<T, Id, M>
            {...selectProps}
            options={options}
            getOptionData={getOptionDataFromHook}
            value={resolvedValue}
            multiple={multiple}
            disabled={disabledProp}
            onChange={handleChange}
            isLoading={blockingOptionsLoading}
            isRefreshing={backgroundOptionsRefreshing}
            statusSlot={statusSlot}
        />
    );
}
